import {
  commitFieldValue,
  FieldWrapper,
  getFieldErrorMessage,
} from "@birdwatch/ui/form";
import type { ReactNode } from "react";

import { DayPicker } from "@/components/form/day-picker";
import {
  type FieldBindingProps,
  PolyField,
  pickBinding,
} from "@/fields/_internal/poly-field";

type DayFieldBaseProps = {
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  /** Applied to the FieldWrapper root. */
  className?: string;
  /** Applied to the button that opens the calendar. */
  inputClassName?: string;
  /** The earliest day (YYYY-MM-DD) the person may pick. */
  min?: string;
  /** The person may choose none, which this names ("No expiry"); absent: a day is required. */
  noneLabel?: string;
  /** A line under the choice shown only while none is chosen (what none means). */
  noneDescription?: ReactNode;
  /** The day the Date tab sets when the person moves from none to a day; null: the calendar opens with none chosen. */
  startDay: string | null;
};

export type DayFieldProps = DayFieldBaseProps & FieldBindingProps;

/**
 * **Value is a calendar day `"YYYY-MM-DD"`, or `null` for none** (only where `noneLabel` offers none). A discrete
 * picker: choosing a day or the none tab is the commit.
 */
export function DayField(props: DayFieldProps) {
  const {
    label,
    description,
    disabled,
    className,
    inputClassName,
    min,
    noneLabel,
    noneDescription,
    startDay,
  } = props;
  const binding = pickBinding(props);
  return (
    <PolyField<string | null> {...binding}>
      {(field) => (
        <FieldWrapper
          label={label}
          description={description}
          error={getFieldErrorMessage(field)}
          className={className}
          disabled={disabled}
        >
          <DayPicker
            value={field.state.value ?? null}
            onChange={(value) => commitFieldValue(field, value)}
            min={min}
            noneLabel={noneLabel}
            noneDescription={noneDescription}
            disabled={disabled}
            startDay={startDay}
            className={inputClassName}
          />
        </FieldWrapper>
      )}
    </PolyField>
  );
}
